import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, User, Stethoscope, Bike, AlertCircle, Clock, CheckCircle, XCircle, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import Table from '../components/Table';
import FilterBar from '../components/FilterBar';
import Modal from '../components/Modal';
import { useFetch, useFilters } from '../hooks';
import { supportTicketService } from '../services/api';
import { formatDate } from '../utils/helpers';

/* ── Status config ──────────────────────────────────────────────────────────── */
export const TICKET_STATUS_CONFIG = {
  pending:     { label: 'Pending',     cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300', icon: Clock       },
  resolved:    { label: 'Resolved',    cls: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',     icon: CheckCircle },
  rejected:    { label: 'Rejected',    cls: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',             icon: XCircle     },
};

export const PRIORITY_CONFIG = {
  high:   { label: 'High',   cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'         },
  medium: { label: 'Medium', cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
  low:    { label: 'Low',    cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'         },
};

export const TicketStatusBadge = ({ status }) => {
  const cfg = TICKET_STATUS_CONFIG[status] || { label: status, cls: 'bg-gray-100 text-gray-600' };
  const Icon = cfg.icon || AlertCircle;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${cfg.cls}`}>
      <Icon className="w-3 h-3" />{cfg.label}
    </span>
  );
};

export const PriorityBadge = ({ priority }) => {
  const cfg = PRIORITY_CONFIG[priority] || { label: priority, cls: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
};

/* ── User type badge ────────────────────────────────────────────────────────── */
const USER_TYPE_CFG = {
  Patient:  { cls: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300', icon: User       },
  Doctor:   { cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',         icon: Stethoscope },
  Delivery: { cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300', icon: Bike       },
};

const UserTypeBadge = ({ type }) => {
  const cfg = USER_TYPE_CFG[type] || { cls: 'bg-gray-100 text-gray-600', icon: User };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${cfg.cls}`}>
      <Icon className="w-3 h-3" />{type}
    </span>
  );
};

/* ── Filter config ──────────────────────────────────────────────────────────── */
const FILTER_INITIAL = { search: '', status: '', userType: '', dateFrom: '', dateTo: '' };
const FILTER_FIELDS = [
  {
    key: 'status', label: 'Status', type: 'select',
    options: Object.entries(TICKET_STATUS_CONFIG).map(([value, { label }]) => ({ value, label })),
  },
  {
    key: 'userType', label: 'User Type', type: 'select',
    options: [
      { value: 'patient',  label: 'Patient'  },
      { value: 'doctor',   label: 'Doctor'   },
      { value: 'delivery', label: 'Delivery' },
    ],
  },
  { key: 'dateFrom', label: 'From Date', type: 'date' },
  { key: 'dateTo',   label: 'To Date',   type: 'date' },
];

const ticketFilterFn = (t, f) => {
  if (f.search) {
    const q = f.search.toLowerCase();
    if (![t.id, t.user_name, t.title].join(' ').toLowerCase().includes(q)) return false;
  }
  if (f.status   && t.status    !== f.status)   return false;
  if (f.userType && t.user_type !== f.userType.toLowerCase()) return false;
  const date = t.created_at?.split('T')[0];
  if (f.dateFrom && date < f.dateFrom) return false;
  if (f.dateTo   && date > f.dateTo)   return false;
  return true;
};

/* ── Create Ticket Modal ────────────────────────────────────────────────────── */
const CreateTicketModal = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState({
    userName: '', userEmail: '', userPhone: '',
    userType: 'Patient', title: '', description: '',
    priority: 'medium',
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const id = `TKT-${String(Date.now()).slice(-4)}`;
      await supportTicketService.create({
        ...form,
        id,
        userId: null,
        status: 'open',
        image: null, imageType: null,
        assignedTo: null,
        notes: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      toast.success('Ticket created!');
      onSuccess(); onClose();
    } catch { toast.error('Failed to create ticket'); }
    finally { setLoading(false); }
  };

  return (
    <Modal isOpen onClose={onClose} title="Create Support Ticket" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">User Name <span className="text-red-500">*</span></label>
            <input value={form.userName} onChange={e => set('userName', e.target.value)} className="input-field" placeholder="e.g. John Smith" required />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">User Type <span className="text-red-500">*</span></label>
            <select value={form.userType} onChange={e => set('userType', e.target.value)} className="input-field">
              <option value="Patient">Patient</option>
              <option value="Doctor">Doctor</option>
              <option value="Delivery">Delivery</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
            <input type="email" value={form.userEmail} onChange={e => set('userEmail', e.target.value)} className="input-field" placeholder="user@email.com" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
            <input value={form.userPhone} onChange={e => set('userPhone', e.target.value)} className="input-field" placeholder="+1-555-0000" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Issue Title <span className="text-red-500">*</span></label>
            <input value={form.title} onChange={e => set('title', e.target.value)} className="input-field" placeholder="Brief summary of the issue" required />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Description <span className="text-red-500">*</span></label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} className="input-field resize-none" rows={3} placeholder="Detailed description of the issue…" required />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</label>
            <select value={form.priority} onChange={e => set('priority', e.target.value)} className="input-field">
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-1">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" disabled={loading} className="btn-primary disabled:opacity-50">
            {loading ? 'Creating…' : 'Create Ticket'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

/* ── Page ───────────────────────────────────────────────────────────────────── */
const SupportTickets = () => {
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const { data: tickets, loading, refetch } = useFetch(() => supportTicketService.getAll());

  const filterFnCb = useCallback(ticketFilterFn, []);
  const { filters, onChange: onFilterChange, onReset, filtered, activeCount } = useFilters(
    FILTER_INITIAL, filterFnCb, tickets
  );

  /* Summary counts */
  const counts = tickets ? {
    total:    tickets.length,
    pending:  tickets.filter(t => t.status === 'pending').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
    rejected: tickets.filter(t => t.status === 'rejected').length,
  } : {};

  const summaryCards = [
    { label: 'Total',    value: counts.total    || 0, color: 'text-gray-900 dark:text-white',        bg: '' },
    { label: 'Pending',  value: counts.pending  || 0, color: 'text-yellow-600 dark:text-yellow-400', bg: 'bg-yellow-50 dark:bg-yellow-900/10' },
    { label: 'Resolved', value: counts.resolved || 0, color: 'text-green-600 dark:text-green-400',   bg: 'bg-green-50 dark:bg-green-900/10' },
    { label: 'Rejected', value: counts.rejected || 0, color: 'text-red-600 dark:text-red-400',       bg: 'bg-red-50 dark:bg-red-900/10' },
  ];

  const columns = [
    {
      key: 'id', label: 'Ticket ID',
      render: v => (
        <span className="font-mono text-xs font-semibold text-medihub-primary dark:text-medihub-highlight">#{v}</span>
      ),
    },
    {
      key: 'user_name', label: 'User',
      render: (v, row) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-white text-sm">{v}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{row.user_mobile}</p>
        </div>
      ),
    },
    {
      key: 'user_type', label: 'User Type',
      render: v => <UserTypeBadge type={v?.charAt(0).toUpperCase() + v?.slice(1)} />,
    },
    {
      key: 'title', label: 'Issue Title',
      render: (v, row) => (
        <div className="max-w-[220px]">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{v}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{row.description}</p>
        </div>
      ),
    },
    {
      key: 'status', label: 'Status',
      render: v => <TicketStatusBadge status={v} />,
    },
    {
      key: 'admin_note', label: 'Admin Note',
      render: v => v ? <span className="text-xs text-gray-600 dark:text-gray-400 max-w-[150px] truncate block">{v}</span> : <span className="text-xs text-gray-400">—</span>,
    },
    {
      key: 'created_at', label: 'Created',
      render: v => <span className="text-sm text-gray-600 dark:text-gray-400">{formatDate(v?.split('T')[0])}</span>,
    },
    {
      key: 'id', label: 'Actions',
      render: (_, row) => (
        <button onClick={() => navigate(`/support/${row.id}`)}
          className="p-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors text-blue-500" title="View Ticket">
          <Eye className="w-4 h-4" />
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Support Tickets</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Manage and resolve user support queries</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-1.5">
          <Plus className="w-4 h-4" />Create Ticket
        </button>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {summaryCards.map(({ label, value, color, bg }) => (
          <div key={label} className={`card p-4 text-center ${bg}`}>
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <FilterBar
        filters={filters}
        onChange={onFilterChange}
        onReset={onReset}
        searchPlaceholder="Search by ticket ID or user name…"
        fields={FILTER_FIELDS}
        activeCount={activeCount}
      />

      {/* Table */}
      <Table
        title={`Support Tickets (${filtered.length}${activeCount ? ` of ${tickets?.length || 0}` : ''})`}
        columns={columns}
        data={filtered}
        loading={loading}
        exportFilename="support-tickets"
      />

      {showCreate && (
        <CreateTicketModal onClose={() => setShowCreate(false)} onSuccess={refetch} />
      )}
    </div>
  );
};

export default SupportTickets;
